import { useEffect, useRef, useState } from 'react';
import './App.css';

const APP_URL = 'https://genomatch-ebf35.web.app';

const GENOTYPE_COMPAT_ROWS = [
  { partners: 'AA + AA', outcomes: 'AA, AA, AA, AA', compatible: true },
  { partners: 'AA + AS', outcomes: 'AA, AS, AA, AS', compatible: true },
  { partners: 'AA + AC', outcomes: 'AA, AA, AC, AC', compatible: true },
  { partners: 'AA + SS', outcomes: 'AS, AS, AS, AS', compatible: true },
  { partners: 'AA + SC', outcomes: 'AS, AC, AS, AC', compatible: true },
  { partners: 'AA + CC', outcomes: 'AC, AC, AC, AC', compatible: true },
  { partners: 'AS + AS', outcomes: 'AA, AS, AS, SS', compatible: false },
  { partners: 'AS + AC', outcomes: 'AA, AC, AS, SC', compatible: false },
  { partners: 'AS + SS', outcomes: 'AS, AS, SS, SS', compatible: false },
  { partners: 'SS + SS', outcomes: 'SS, SS, SS, SS', compatible: false },
  { partners: 'AC + AC', outcomes: 'AA, AC, AC, CC', compatible: false },
  { partners: 'AC + SS', outcomes: 'AS, AS, SC, SC', compatible: false },
  { partners: 'AC + CC', outcomes: 'AC, AC, CC, CC', compatible: false },
  { partners: 'CC + CC', outcomes: 'CC, CC, CC, CC', compatible: false },
  { partners: 'SC + SC', outcomes: 'SS, SC, SC, CC', compatible: false },
];

/* ——— SVG icons ——— */
function IconHeart({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  );
}

function IconPerson() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function IconDNA() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M4 4c4 4 4 16 8 16s4-12 8-16M4 20c4-4 4-16 8-16s4 12 8 16" />
      <path d="M9 9h6M9 15h6" strokeLinecap="round" />
    </svg>
  );
}

function IconVerified() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        fill="currentColor"
        d="M12 2l2.4 4.9L20 8l-4 3.9L17.5 20 12 17.2 6.5 20 8 11.9 4 8l5.6-1.1L12 2z"
        opacity="0.15"
      />
      <path
        fill="currentColor"
        d="M12 3.2l2 4.1 4.5.9-3.3 3.2.8 4.5L12 16.4l-4 2.1.8-4.5L5.5 8.2l4.5-.9 2-4.1z"
      />
      <path stroke="currentColor" strokeWidth="1.5" d="M9 12l2 2 4-4" fill="none" />
    </svg>
  );
}

function IconChatLock() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
      <circle cx="12" cy="16" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconSliders() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M9 10h6M15 15h6" strokeLinecap="round" />
    </svg>
  );
}

function IconBell() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" strokeLinecap="round" />
    </svg>
  );
}

function IconShield() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinejoin="round" />
    </svg>
  );
}

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.12, rootMargin: '0px 0px -36px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ as: Tag = 'div', className = '', children, style }) {
  const [ref, visible] = useReveal();
  return (
    <Tag ref={ref} className={`reveal ${visible ? 'visible' : ''} ${className}`.trim()} style={style}>
      {children}
    </Tag>
  );
}

function App() {
  const [openFaq, setOpenFaq] = useState(null);

  const faqItems = [
    {
      q: 'What is genotype compatibility?',
      a: "Genotype compatibility refers to how two people's genetic types interact. Certain combinations like AS and AS can lead to children with sickle cell disease. GenoMatch helps you find partners whose genotype works well with yours.",
    },
    {
      q: 'Is my genotype information private?',
      a: 'Yes completely. Your genotype is encrypted and only used to filter your matches. It is never shared publicly or sold to third parties.',
    },
    {
      q: 'Do I need a lab report to join?',
      a: 'You can join and browse without a report but we encourage uploading one for verification. Verified members get more trust and better matches.',
    },
    {
      q: 'Is GenoMatch only for people with SS genotype?',
      a: 'No. GenoMatch is for everyone regardless of genotype. We help all users make informed decisions about compatibility.',
    },
    {
      q: 'Is the app free to use?',
      a: 'Yes GenoMatch is free to join and use. Premium features are available for members who want enhanced matching and visibility.',
    },
    {
      q: 'Which countries is GenoMatch available in?',
      a: 'GenoMatch is currently available in the UK and expanding to more countries soon.',
    },
  ];

  return (
    <div className="gm-page">
      <main>
        <section className="gm-hero" id="hero">
          <div className="gm-hero__mobile-bar">
            <span className="gm-hero__brand gm-serif">GenoMatch</span>
          </div>
          <div className="gm-hero__grid">
            <Reveal className="gm-hero__left">
              <span className="gm-hero__badge">Smart Genotype Matching</span>
              <h1 className="gm-hero__headline gm-serif">
                Find Love That
                <br />
                Lasts a Lifetime
              </h1>
              <p className="gm-hero__sub">
                GenoMatch is the first dating app that matches you based on genotype compatibility — helping you build a
                healthy, happy future together
              </p>
              <div className="gm-hero__ctas">
                <a className="gm-btn gm-btn--primary gm-btn--lg" href={APP_URL}>
                  Get Started For Free
                </a>
              </div>
              <div className="gm-hero__social">
                <p className="gm-hero__social-text">Where Compatibility Meets Love</p>
                <ul className="gm-hero__social-list">
                  <li>Genotype-compatible matching</li>
                  <li>Meaningful Connections</li>
                  <li>Free to join</li>
                </ul>
              </div>
              <div className="gm-hero__mobile-highlights" aria-label="GenoMatch highlights">
                <p className="gm-hero__mobile-highlight-main">Where Compatibility Meets Love</p>
                <p className="gm-hero__mobile-highlight">
                  <span className="gm-hero__mobile-highlight-bar" aria-hidden />
                  Genotype-compatible matching
                </p>
                <p className="gm-hero__mobile-highlight">
                  <span className="gm-hero__mobile-highlight-bar" aria-hidden />
                  Meaningful Connections
                </p>
                <p className="gm-hero__mobile-highlight">
                  <span className="gm-hero__mobile-highlight-bar" aria-hidden />
                  Free to join
                </p>
              </div>
            </Reveal>

            <div className="gm-hero__right">
              <Reveal className="gm-hero__visual" style={{ width: '100%' }}>
                <div className="gm-hero__blob gm-hero__blob--1" />
                <div className="gm-hero__blob gm-hero__blob--2" />
                <IconHeart className="gm-float-heart" />
                <IconHeart className="gm-float-heart" />
                <IconHeart className="gm-float-heart" />
                <div className="gm-phone">
                  <div className="gm-phone__notch" aria-hidden />
                  <div className="gm-phone__screen">
                    <div className="gm-phone__header">
                      <span>Discover</span>
                      <span>London</span>
                    </div>
                    <div className="gm-phone__card">
                      <div className="gm-phone__card-photo">
                        <img
                          className="gm-phone__card-img"
                          src="https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=800&q=100&fit=crop"
                          alt=""
                          width={400}
                          height={533}
                          loading="lazy"
                          decoding="async"
                        />
                        <div className="gm-phone__card-photo-overlay" aria-hidden />
                        <span className="gm-phone__card-badge">94% match</span>
                      </div>
                      <div className="gm-phone__card-body">
                        <p className="gm-phone__card-name">Amara, 28</p>
                        <p className="gm-phone__card-meta">Genotype · AA · 2 km away</p>
                        <p className="gm-phone__card-bio">
                          Creative soul who loves art, music and deep conversations. Looking for something real and lasting.
                        </p>
                        <div className="gm-phone__card-tags">
                          <span className="gm-phone__tag">Art</span>
                          <span className="gm-phone__tag">Running</span>
                          <span className="gm-phone__tag">Cooking</span>
                        </div>
                      </div>
                    </div>
                    <div className="gm-phone__actions">
                      <button type="button" className="gm-phone__action gm-phone__action--pass" aria-label="Pass">
                        ✕
                      </button>
                      <button type="button" className="gm-phone__action gm-phone__action--like" aria-label="Like">
                        <IconHeart />
                      </button>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="gm-section gm-section--blush" id="how-it-works">
          <Reveal className="gm-section-head">
            <h2 className="gm-serif">How GenoMatch Works</h2>
            <p>Finding your compatible partner has never been easier</p>
          </Reveal>
          <div className="gm-how__steps">
            <Reveal className="gm-step">
              <div className="gm-step__icon">
                <IconPerson />
              </div>
              <h3>Create Your Profile</h3>
              <p>
                Sign up and tell us about yourself — your personality, lifestyle and what you are looking for in a partner
              </p>
            </Reveal>
            <Reveal className="gm-step">
              <div className="gm-step__icon">
                <IconDNA />
              </div>
              <h3>Enter Your Genotype</h3>
              <p>
                Select your genotype from AA AS SS AC SC or CC. We use this to filter and suggest your most compatible
                matches
              </p>
            </Reveal>
            <Reveal className="gm-step">
              <div className="gm-step__icon">
                <IconHeart />
              </div>
              <h3>Match &amp; Connect</h3>
              <p>Browse compatible profiles, like the ones you are interested in and start chatting when you both match</p>
            </Reveal>
          </div>
        </section>

        <section className="gm-section gm-section--blush" id="features">
          <Reveal className="gm-section-head">
            <h2 className="gm-serif">Everything You Need to Find the One</h2>
            <p>Powerful features designed to help you make informed and healthy relationship decisions</p>
          </Reveal>
          <div className="gm-features__grid">
            {[
              {
                Icon: IconDNA,
                title: 'Smart Genotype Matching',
                text: 'Our algorithm only shows you partners whose genotype is compatible with yours — no more guessing',
              },
              {
                Icon: IconVerified,
                title: 'Verified Profiles',
                text: 'Every member verifies their genotype with a lab report so you can trust the matches you see',
              },
              {
                Icon: IconChatLock,
                title: 'Safe & Private Chat',
                text: 'Message your matches in a secure encrypted chat. Your conversations are completely private',
              },
              {
                Icon: IconSliders,
                title: 'Advanced Filters',
                text: 'Filter by location, age, religion, lifestyle and genotype preferences to find exactly who you are looking for',
              },
              {
                Icon: IconBell,
                title: 'Instant Match Alerts',
                text: 'Get notified the moment someone compatible likes you back so you never miss a connection',
              },
              {
                Icon: IconShield,
                title: 'Your Data is Safe',
                text: 'Your genotype and personal information is fully encrypted and never shared without your explicit consent',
              },
            ].map(({ Icon, title, text }) => (
              <Reveal key={title} className="gm-feature-card">
                <div className="gm-feature-card__icon">
                  <Icon />
                </div>
                <h3>{title}</h3>
                <p>{text}</p>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="gm-why-text" id="why-genomatch">
          <Reveal className="gm-why-text__inner">
            <h2 className="gm-serif">Why Genotype Compatibility Matters</h2>
            <p>
              Your genotype is inherited from your parents and can influence the genetic possibilities for your future
              children.
            </p>
            <p>
              Knowing your genotype and understanding how it compares with a potential partner&apos;s can help you make
              informed decisions about family planning.
            </p>
            <p>
              GenoMatch makes this easier by helping you discover compatibility early, before emotions get too deep.
            </p>
            <p>
              It&apos;s not about deciding who you can love. It&apos;s about having the information to choose with
              clarity.
            </p>
            <div className="gm-why-text__disclaimer">
              Compatibility information is for guidance only. Always consult a qualified medical professional for health
              decisions.
            </div>
          </Reveal>
        </section>

        <section className="gm-compat-section" id="genotype-compatibility">
          <Reveal className="gm-compat-section__inner">
            <div className="gm-compat-section__head">
              <h2 className="gm-serif">
                Genotype <span className="gm-compat-section__accent">Compatibility</span> at a glance
              </h2>
              <p>
                Understanding how genotypes pair helps you make informed choices and build a healthy future together.
              </p>
            </div>
            <div className="gm-compat-table-wrap">
              <table className="gm-compat-table">
                <thead>
                  <tr>
                    <th scope="col">Partners</th>
                    <th scope="col">Possible Genotype Outcomes</th>
                    <th scope="col">Compatibility</th>
                  </tr>
                </thead>
                <tbody>
                  {GENOTYPE_COMPAT_ROWS.map((row) => (
                    <tr key={row.partners} className={row.compatible ? 'gm-compat-table__row--ok' : 'gm-compat-table__row--no'}>
                      <td className="gm-compat-table__partners">{row.partners}</td>
                      <td className="gm-compat-table__outcomes">{row.outcomes}</td>
                      <td className="gm-compat-table__status">
                        <span
                          className={`gm-compat-table__badge ${
                            row.compatible ? 'gm-compat-table__badge--ok' : 'gm-compat-table__badge--no'
                          }`}
                        >
                          {row.compatible ? 'Compatible' : 'Incompatible'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="gm-compat-section__note">
              <span className="gm-compat-section__note-icon" aria-hidden>
                i
              </span>
              <p>
                The chart shows <strong>possible genotype outcomes, not the order</strong> your children will be born.
                Each pregnancy is independent, so the same genotype outcome can occur more than once.
              </p>
            </div>
          </Reveal>
        </section>

        <section className="gm-section gm-section--blush" id="testimonials">
          <Reveal className="gm-section-head">
            <h2 className="gm-serif">Stories from Our Members</h2>
            <p>Real people finding compatible love with GenoMatch</p>
          </Reveal>
          <div className="gm-testimonials__grid">
            {[
              {
                quote:
                  'I never thought about genotype compatibility before GenoMatch. Now I am engaged to someone perfectly compatible and I could not be happier. This app changed my life',
                name: 'Fatima A.',
                loc: 'London',
                img: '/images/testimonials/fatima-a.jpg',
              },
              {
                quote:
                  'As someone with AS genotype I was always worried about finding a compatible partner. GenoMatch made it so easy and stress free',
                name: 'David O.',
                loc: 'Manchester',
                img: '/images/testimonials/david-o.jpg',
              },
              {
                quote:
                  'The app is beautiful, easy to use and the matches are so relevant. I met my partner within 2 weeks of joining',
                name: 'Aisha M.',
                loc: 'Birmingham',
                img: 'https://i.pravatar.cc/120?img=68',
              },
              {
                quote:
                  'Finally an app that understands why genotype matters. I felt seen and matched with people who truly fit my life goals',
                name: 'Marcus T.',
                loc: 'London',
                img: 'https://i.pravatar.cc/120?img=52',
              },
            ].map((t) => (
              <Reveal key={t.name} className="gm-testimonial">
                <div className="gm-testimonial__mark" aria-hidden>
                  “
                </div>
                <p className="gm-testimonial__quote">{t.quote}</p>
                <div className="gm-testimonial__author">
                  <img className="gm-testimonial__photo" src={t.img} alt="" />
                  <div>
                    <strong>{t.name}</strong>
                    <div className="gm-testimonial__loc">{t.loc}</div>
                  </div>
                </div>
                <div className="gm-stars" aria-label="5 out of 5 stars">
                  ★★★★★
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="gm-cta-download" id="download">
          <Reveal>
            <h2 className="gm-serif">Ready to Find Your Perfect Match?</h2>
            <p className="gm-cta-download__sub">
              Join early members already finding compatible love on GenoMatch. Free to join.
            </p>
            <div className="gm-cta-download__btns">
              <a className="gm-btn gm-btn--lg gm-btn--white" href={APP_URL}>
                Get Started For Free
              </a>
              <a className="gm-btn gm-btn--lg gm-btn--white-outline" href={APP_URL}>
                Learn More
              </a>
            </div>
          </Reveal>
        </section>

        <section className="gm-section gm-section--warm" id="faq">
          <Reveal className="gm-section-head">
            <h2 className="gm-serif">Frequently Asked Questions</h2>
            <p>Everything you need to know about GenoMatch</p>
          </Reveal>
          <div className="gm-faq__list">
            {faqItems.map((item, i) => (
              <div key={item.q} className={`gm-faq__item ${openFaq === i ? 'open' : ''}`}>
                <button
                  type="button"
                  className="gm-faq__question"
                  aria-expanded={openFaq === i}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="gm-faq__question-text">{item.q}</span>
                  <span className="gm-faq__icon" aria-hidden>
                    {openFaq === i ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14" />
                      </svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                    )}
                  </span>
                </button>
                <div className="gm-faq__answer-wrap">
                  <div className="gm-faq__answer-inner">
                    <p className="gm-faq__answer">{item.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default App;
